import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardMedia,
  Grid, 
  Divider, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import axios from '../utils/axiosConfig';
import { handleApiError } from '../utils/apiErrorHandler';
import { CompetitionInfoContext } from './RegisteredCompetitionPage';

interface CompetitionDetails {
  _id: string;
  name: string;
  location: string;
  venue: {
    name: string;
    address: string;
    mapLink?: string;
  };
  startDate: string;
  endDate: string;
  contactPerson: {
    name: string;
    email: string;
    phone: string;
  };
  schedule: {
    doors: string;
    startTime: string;
    endTime: string;
  };
}

interface Announcement {
  id: string;
  date: string;
  title: string;
  content: string;
  isNew: boolean;
}

interface Document {
  id: string;
  title: string;
  description: string;
  fileType: string;
  fileSize: string;
  uploadDate: string;
  downloadUrl: string;
}

const CompetitionInfoPage: React.FC = () => {
  const { competitionId } = useParams();
  const [competitionDetails, setCompetitionDetails] = useState<CompetitionDetails | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Access the validation context
  const infoContext = useContext(CompetitionInfoContext);

  useEffect(() => {
    const fetchCompetitionInfo = async () => {
      try {
        setLoading(true);
        // These endpoints would fetch competition details, announcements, and documents
        // const detailsResponse = await axios.get(`/api/competitions/${competitionId}/details`);
        // const announcementsResponse = await axios.get(`/api/competitions/${competitionId}/announcements`);
        // const documentsResponse = await axios.get(`/api/competitions/${competitionId}/documents`);
        
        // Mock data for demonstration
        const mockDetails: CompetitionDetails = {
          _id: competitionId || '1',
          name: 'Deutsche Meisterschaft 2025',
          location: 'Berlin',
          venue: {
            name: 'Sportpalast Berlin',
            address: 'Sportstraße 123, 10115 Berlin',
            mapLink: 'https://maps.google.com'
          },
          startDate: '2025-04-15',
          endDate: '2025-04-17',
          contactPerson: {
            name: 'Thomas Weber',
            email: 'tweber@sportverband.de',
            phone: '+49 123 4567890'
          },
          schedule: {
            doors: '08:00',
            startTime: '09:00',
            endTime: '18:00'
          }
        };
        
        const mockAnnouncements: Announcement[] = [
          {
            id: 'a1',
            date: '2025-03-20',
            title: 'Aktualisierte Wettkampfregeln',
            content: 'Bitte beachten Sie die aktualisierten Wettkampfregeln für die diesjährige Meisterschaft. Die Änderungen betreffen hauptsächlich die Bewertungskriterien in der Elite-Klasse.',
            isNew: true
          },
          {
            id: 'a2',
            date: '2025-03-15',
            title: 'Informationen zur Anreise',
            content: 'Aufgrund von Bauarbeiten ist die direkte Zufahrt zum Veranstaltungsort eingeschränkt. Bitte nutzen Sie die ausgeschilderten Umleitungen oder öffentliche Verkehrsmittel.',
            isNew: false
          },
          {
            id: 'a3',
            date: '2025-03-10',
            title: 'Verpflegung vor Ort',
            content: 'Im Sportpalast stehen während der gesamten Veranstaltung mehrere Cateringbereiche zur Verfügung. Es werden sowohl Snacks als auch vollwertige Mahlzeiten angeboten.',
            isNew: false
          }
        ];
        
        const mockDocuments: Document[] = [
          {
            id: 'd1',
            title: 'Zeitplan - Finale Version',
            description: 'Detaillierter Zeitplan für alle Wettkampftage',
            fileType: 'PDF',
            fileSize: '1.2 MB',
            uploadDate: '2025-03-22',
            downloadUrl: '#'
          },
          {
            id: 'd2',
            title: 'Hallenplan',
            description: 'Übersicht über alle Bereiche der Wettkampfhalle',
            fileType: 'PDF',
            fileSize: '0.8 MB',
            uploadDate: '2025-03-18',
            downloadUrl: '#'
          },
          {
            id: 'd3',
            title: 'Starterlisten',
            description: 'Endgültige Starterlisten für alle Kategorien',
            fileType: 'PDF',
            fileSize: '1.5 MB',
            uploadDate: '2025-03-20',
            downloadUrl: '#'
          },
          {
            id: 'd4',
            title: 'Regelwerk 2025',
            description: 'Offizielles Regelwerk für die diesjährige Meisterschaft',
            fileType: 'PDF',
            fileSize: '2.3 MB',
            uploadDate: '2025-03-15',
            downloadUrl: '#'
          }
        ];
        
        setCompetitionDetails(mockDetails);
        setAnnouncements(mockAnnouncements);
        setDocuments(mockDocuments);
        setLoading(false);
      } catch (error) {
        handleApiError(error, 'Fehler beim Laden der Wettkampfinformationen.');
        setLoading(false);
      }
    };

    if (competitionId) {
      fetchCompetitionInfo();
    }
  }, [competitionId]);

  const hasNewAnnouncements = announcements.some(a => a.isNew);

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Wettkampfinformationen
        </Typography>
        
        {hasNewAnnouncements && (
          <Alert 
            severity="info" 
            icon={<NewReleasesIcon />}
            sx={{ mb: 3 }}
          >
            Es gibt neue Ankündigungen für diesen Wettkampf!
          </Alert>
        )}
        
        {loading ? (
          <Typography>Lade Daten...</Typography>
        ) : (
          <Grid container spacing={3}>
            {/* Veranstaltungsort */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Veranstaltungsort
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary={competitionDetails?.venue.name} 
                            secondary={competitionDetails?.venue.address}
                          />
                        </ListItem>
                        <ListItem>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            component="a"
                            href={competitionDetails?.venue.mapLink || '#'} 
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LocationOnIcon />}
                          >
                            Karte öffnen
                          </Button>
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <CardMedia
                        component="img"
                        height="120"
                        image="/api/placeholder/400/320"
                        alt="Veranstaltungsort"
                        sx={{ borderRadius: 1 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Zeitplaninformationen */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Zeiten
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <EventIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Veranstaltungszeitraum" 
                        secondary={`${new Date(competitionDetails?.startDate || '').toLocaleDateString()} - ${new Date(competitionDetails?.endDate || '').toLocaleDateString()}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Einlass" 
                        secondary={`${competitionDetails?.schedule.doors} Uhr`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Wettkampfzeiten" 
                        secondary={`${competitionDetails?.schedule.startTime} - ${competitionDetails?.schedule.endTime} Uhr`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Kontaktinformationen */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Kontakt
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Ansprechpartner" 
                        secondary={competitionDetails?.contactPerson.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={competitionDetails?.contactPerson.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={competitionDetails?.contactPerson.phone} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Ankündigungen */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Ankündigungen
              </Typography>
              {announcements.length > 0 ? (
                announcements.map(announcement => (
                  <Accordion key={announcement.id} defaultExpanded={announcement.isNew}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ flexGrow: 1 }}>
                        {announcement.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(announcement.date).toLocaleDateString()}
                        </Typography>
                        {announcement.isNew && (
                          <Chip 
                            label="Neu" 
                            color="primary" 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        {announcement.content}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Keine Ankündigungen vorhanden
                </Typography>
              )}
            </Grid>

            {/* Dokumente */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Dokumente
              </Typography>
              <Grid container spacing={2}>
                {documents.map(doc => (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {doc.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {doc.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Box>
                            <Chip 
                              label={doc.fileType} 
                              size="small" 
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {doc.fileSize}
                            </Typography>
                          </Box>
                          <Button 
                            variant="contained" 
                            size="small" 
                            href={doc.downloadUrl}
                          >
                            Download
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {documents.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Keine Dokumente vorhanden
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default CompetitionInfoPage;